import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Button from '../../components/ui/Button.jsx';
import StudyPlanPrintLayout from '../../components/StudyPlanPrintLayout.jsx';
import { useApi } from '../../hooks/useApi.js';
import { usePrintPlan } from '../../hooks/usePrintPlan.js';

export default function StudyPlan() {
  const { data, loading, error } = useApi('/api/students/me');
  const { printDate, handlePrint } = usePrintPlan();

  if (loading) {
    return (
      <div className="page study-plan-page">
        <div className="page-header print-hide">
          <div>
            <h1>Study Plan</h1>
            <p className="subtle">Track your course categories and requirements.</p>
          </div>
        </div>
        <p className="subtle">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page study-plan-page">
        <div className="page-header print-hide">
          <div>
            <h1>Study Plan</h1>
            <p className="subtle">Track your course categories and requirements.</p>
          </div>
        </div>
        <div className="alert error">{error}</div>
      </div>
    );
  }

  const plan = data || {};
  const cat3 = joinCourses([plan.cat31, plan.cat32]);
  const cat4 = joinCourses([plan.cat41, plan.cat42]);
  const electives = joinCourses([plan.ec1, plan.ec2, plan.ec3, plan.ec4, plan.ec5, plan.ec6]);
  const qualCourses = joinCourses([plan.qc1, plan.qc2, plan.qc3, plan.qc4, plan.qc5]);
  const prereqCourses = joinCourses([plan.pc1, plan.pc2, plan.pc3, plan.pc4, plan.pc5]);
  const studentName = fullName(plan) || plan.username || '—';

  return (
    <div className="page study-plan-page">
      <div className="page-header print-hide">
        <div>
          <h1>Study Plan</h1>
          <p className="subtle">Track your course categories and requirements.</p>
        </div>
        <div className="header-actions print-hide">
          <Badge tone="info">Updated</Badge>
          <Button variant="secondary" type="button" onClick={handlePrint}>Print Study Plan</Button>
        </div>
      </div>

      <StudyPlanPrintLayout
        plan={plan}
        studentName={studentName}
        cin={plan.cin || '—'}
        campusEmail={plan.username || '—'}
        printDate={printDate}
      />

      <div className="grid two print-hide">
        <Card title="Core Categories">
          <div className="plan-list">
            <div className="plan-row">
              <span>Category 1</span>
              <span>{plan.cat1 || '—'}</span>
            </div>
            <div className="plan-row">
              <span>Category 2</span>
              <span>{plan.cat2 || '—'}</span>
            </div>
            <div className="plan-row">
              <span>Category 3</span>
              <span>{cat3 || '—'}</span>
            </div>
            <div className="plan-row">
              <span>Category 4</span>
              <span>{cat4 || '—'}</span>
            </div>
          </div>
        </Card>
        <Card title="Electives & Requirements">
          <div className="plan-list">
            <div className="plan-row">
              <span>Electives</span>
              <span>{electives || '—'}</span>
            </div>
            {plan.qual_needed === 1 ? (
              <div className="plan-row">
                <span>Qualification Courses</span>
                <span>{qualCourses || '—'}</span>
              </div>
            ) : null}
            {plan.prereq_needed === 1 ? (
              <div className="plan-row">
                <span>Prerequisite Courses</span>
                <span>{prereqCourses || '—'}</span>
              </div>
            ) : null}
          </div>
        </Card>
      </div>

      <Card title="Completion Options" className="print-hide">
        <div className="chip-row">
          <span className={`chip ${plan.comp === 1 ? '' : 'muted'}`}>Comprehensive Exam</span>
          <span className={`chip ${plan.thesis === 1 ? '' : 'muted'}`}>Thesis</span>
          <span className={`chip ${plan.wpe === 1 ? '' : 'muted'}`}>WPE Required</span>
          <span className={`chip ${plan.qual_needed === 1 ? '' : 'muted'}`}>Qual Required</span>
          <span className={`chip ${plan.prereq_needed === 1 ? '' : 'muted'}`}>Prereq Required</span>
        </div>
      </Card>
    </div>
  );
}

function joinCourses(list) {
  return list
    .map((item) => (item || '').trim())
    .filter(Boolean)
    .join(', ');
}

function fullName(student) {
  return `${student.first_name || ''} ${student.last_name || ''}`.trim();
}
